document.addEventListener("DOMContentLoaded", () =>
	customElements.define("cart-unlock-manager", CartUnlockManager)
);

class CartUnlockManager extends HTMLElement {
	#observer;
	#bundlerObserver;
	#wishlistButtonObserver;
	#buttonObserver;
	#isUnlocked;
	#container;
	#attempts = 0;
	#lastAttemptTime = 0;
	#cooldownPeriod = 30000;

	connectedCallback() {
		this.#isUnlocked = localStorage.getItem("cartUnlocked") === "true";

		this.disableAddToCart =
			this.getAttribute("data-disable-add-to-cart") === "true";
		this.hideCartIcon = this.getAttribute("data-hide-cart-icon") === "true";
		this.correctPassword = this.getAttribute("data-password");

		this.#container = document.getElementById("main-content") || document;

		this.initComponent();
		this.initWishlistButtons();
		this.initObserver();
		this.applyInitialState();
		this.handleDynamicContent();
	}

	disconnectedCallback() {
		this.#observer?.disconnect();
		this.#bundlerObserver?.disconnect();
		this.#wishlistButtonObserver?.disconnect();
		this.#buttonObserver?.disconnect();

		document.documentElement.removeEventListener(
			"cart:unlock-state-changed",
			this.updateWishlistButtonStyle
		);
		document.removeEventListener("wk:login", this.handleWishlistLogin);
		document.removeEventListener(
			"wk:wishlist:add:success",
			this.updateWishlistButtonStyle
		);
		document.removeEventListener(
			"wk:wishlist:remove:success",
			this.updateWishlistButtonStyle
		);

		if (this.submitPassword && this.passwordInput) {
			this.submitPassword.removeEventListener(
				"click",
				this.handlePasswordSubmit.bind(this)
			);
			this.passwordInput.removeEventListener(
				"keypress",
				this.handleKeyPress.bind(this)
			);
		}
	}

	initComponent() {
		try {
			this.passwordInput = document.getElementById("passwordInput");
			this.submitPassword = document.getElementById("submitPassword");
			this.errorMessage = document.getElementById("errorMessage");
			this.disabledAtcContainer = document.getElementById(
				"disabled-atc-container"
			);
			this.cartIconLinks = document.querySelectorAll(
				'a.header__icon-wrapper[href*="cart"], li.header__linklist-item > a[href*="cart"]'
			);

			if (!this.passwordInput || !this.submitPassword) {
				return;
			}

			this.attachEvents();
		} catch (error) {
			// silent fail
		}
	}

	initObserver() {
		const targetNode =
			document.querySelector(".shopify-section-header") || document.body;
		let debounceTimeout;

		this.#observer = new MutationObserver((mutations) => {
			clearTimeout(debounceTimeout);
			debounceTimeout = setTimeout(() => {
				for (const mutation of mutations) {
					if (
						mutation.type !== "childList" ||
						!mutation.addedNodes.length
					)
						continue;

					for (const node of mutation.addedNodes) {
						if (node.nodeType !== 1) continue;

						if (
							node.matches("cart-drawer") &&
							node.id === "mini-cart" &&
							(node.complete || node.readyState === "complete")
						) {
							this.applyInitialState();
						}

						if (this.#isUnlocked) {
							const lockedElements =
								node.querySelectorAll(".atc-locked");

							lockedElements.forEach((element) => {
								element.classList.remove("atc-locked");
							});
						}

						this.initWishlistButtons();
					}
				}
			}, 100);
		});

		this.#observer.observe(targetNode, {
			childList: true,
			subtree: true,
		});
	}

	applyInitialState() {
		const unlockTimestamp = localStorage.getItem("cartUnlockTimestamp");
		const expirationTime = 8 * 24 * 60 * 60 * 1000;

		this.#isUnlocked = localStorage.getItem("cartUnlocked") === "true";

		if (this.#isUnlocked && unlockTimestamp) {
			const currentTime = new Date().getTime();
			const timeElapsed = currentTime - parseInt(unlockTimestamp);

			if (timeElapsed < expirationTime) {
				this.unlockCart();
			} else {
				this.lockCart();
				this.clearStorageState();
			}
		} else {
			this.lockCart();
		}

		if (this.disableAddToCart && this.hideCartIcon) {
			this.updateCartIconLinks(false);
		}
	}

	unlockCart() {
		requestAnimationFrame(() => {
			const lockedElements =
				this.#container.querySelectorAll(".atc-locked");
			lockedElements.forEach((element) =>
				element.classList.remove("atc-locked")
			);

			if (this.disabledAtcContainer) {
				this.disabledAtcContainer.style.display = "none";
			}

			if (this.disableAddToCart && this.hideCartIcon) {
				this.updateCartIconLinks(true);
			}

			document.documentElement.dispatchEvent(
				new CustomEvent("cart:unlock-state-changed", {
					detail: { isUnlocked: true },
					bubbles: true,
					composed: true,
				})
			);

			document.dispatchEvent(new CustomEvent("ed:filters:rendered"));
			document.dispatchEvent(new CustomEvent("ed:ajaxinate:loaded"));
		});

		this.#isUnlocked = true;
		this.updateStorageState();
	}

	lockCart() {
		if (this.disabledAtcContainer) {
			this.disabledAtcContainer.style.display = "block";
		}
		if (this.disableAddToCart && this.hideCartIcon) {
			this.updateCartIconLinks(false);
		}

		document.documentElement.dispatchEvent(
			new CustomEvent("cart:unlock-state-changed", {
				detail: { isUnlocked: false },
				bubbles: true,
				composed: true,
			})
		);

		this.#isUnlocked = false;
		this.clearStorageState();
	}

	updateStorageState() {
		try {
			const timestamp = new Date().getTime();
			const updateStorage = () => {
				try {
					localStorage.setItem("cartUnlocked", "true");
					localStorage.setItem(
						"cartUnlockTimestamp",
						timestamp.toString()
					);

					document.documentElement.dispatchEvent(
						new CustomEvent("cart:unlock-state-changed", {
							detail: { isUnlocked: true },
							bubbles: true,
							composed: true,
						})
					);
				} catch (error) {
					// silent fail
				}
			};

			if ("requestIdleCallback" in window) {
				requestIdleCallback(() => updateStorage());
			} else {
				setTimeout(updateStorage, 0);
			}
		} catch (error) {
			// silent fail
		}
	}

	clearStorageState() {
		try {
			localStorage.removeItem("cartUnlocked");
			localStorage.removeItem("cartUnlockTimestamp");
		} catch (error) {
			// silent fail
		}
	}

	updateCartIconLinks(isUnlocked) {
		if (this.disableAddToCart && this.hideCartIcon) {
			this.cartIconLinks.forEach((link) => {
				link.classList.toggle("atc-locked", !isUnlocked);
			});
		}
	}

	attachEvents() {
		if (this.submitPassword && this.passwordInput) {
			this.submitPassword.addEventListener(
				"click",
				this.handlePasswordSubmit.bind(this)
			);
			this.passwordInput.addEventListener(
				"keypress",
				this.handleKeyPress.bind(this)
			);
		}
	}

	handlePasswordSubmit(event) {
		event.preventDefault();
		const currentTime = Date.now();

		if (currentTime - this.#lastAttemptTime > this.#cooldownPeriod) {
			this.#attempts = 0;
		}

		if (
			this.#attempts >= 5 &&
			currentTime - this.#lastAttemptTime < this.#cooldownPeriod
		) {
			const remainingTime = Math.ceil(
				(this.#cooldownPeriod - (currentTime - this.#lastAttemptTime)) /
					1000
			);
			this.displayError(
				`Bitte warten Sie ${remainingTime} Sekunden, bevor Sie es erneut versuchen.`
			);
			return;
		}

		const password = this.passwordInput.value.trim();
		const correctPassword = this.correctPassword;

		if (password.toLowerCase() === correctPassword.toLowerCase()) {
			this.passwordInput.value = "";
			this.errorMessage.classList.add("hidden");
			this.#attempts = 0;
			this.unlockCart();
		} else {
			this.passwordInput.value = "";
			this.errorMessage.classList.remove("hidden");
			this.#attempts++;
			this.#lastAttemptTime = currentTime;
			this.displayError("Falsches Passwort. Bitte erneut versuchen.");
		}

		this.passwordInput.value = "";
	}

	handleKeyPress(event) {
		if (event.key === "Enter") {
			event.preventDefault();
			this.submitPassword?.click();
		}
	}

	displayError(message) {
		if (this.errorMessage) {
			this.errorMessage.textContent = message;
			this.errorMessage.classList.remove("hidden");
			setTimeout(() => this.errorMessage.classList.add("hidden"), 2000);
		}
	}

	initWishlistButtons() {
		const styleId = "wishlist-buttons-style";
		let styleElement = document.getElementById(styleId);

		if (!styleElement) {
			styleElement = document.createElement("style");
			styleElement.id = styleId;
			document.head.appendChild(styleElement);
		}

		let updateTimeout = null;
		let lastIsWishlisted = null;

		const updateWishlistButtonStyle = (immediate = false) => {
			const isCartUnlocked =
				localStorage.getItem("cartUnlocked") === "true";
			const isFeatureEnabled = this.disableAddToCart === true;

			const isCartLocked = this.disableAddToCart && !isCartUnlocked;

			const desktopWishlistButton = document.querySelector(
				"wishlist-button-block:not(#wishlist-button-mobile) wk-button > button"
			);
			let isWishlisted = false;

			if (desktopWishlistButton) {
				isWishlisted =
					desktopWishlistButton.classList.contains("wk-selected");
			}

			if (!immediate && isWishlisted === lastIsWishlisted) {
				if (updateTimeout) {
					clearTimeout(updateTimeout);
				}
				updateTimeout = setTimeout(() => {
					updateWishlistButtonStyle(true);
				}, 150);
				return;
			}

			lastIsWishlisted = isWishlisted;

			if (updateTimeout) {
				clearTimeout(updateTimeout);
				updateTimeout = null;
			}

			const wishlistButtonBlock = document.querySelector(
				"wishlist-button-block:not(#wishlist-button-mobile)"
			);

			if (wishlistButtonBlock && isCartLocked) {
				wishlistButtonBlock.style.setProperty(
					"--wishlist-btn-border-color",
					"#000"
				);
				wishlistButtonBlock.style.setProperty(
					"--wishlist-btn-bg-color",
					"#000"
				);
				wishlistButtonBlock.style.setProperty(
					"--wishlist-btn-text-color",
					"#fff"
				);
				wishlistButtonBlock.style.setProperty(
					"--wishlist-icon-stroke",
					"#fff"
				);
				wishlistButtonBlock.style.setProperty(
					"--wishlist-icon-fill",
					isWishlisted ? "#fff" : "transparent"
				);
			} else if (wishlistButtonBlock && !isCartLocked) {
				wishlistButtonBlock.style.removeProperty(
					"--wishlist-btn-border-color"
				);
				wishlistButtonBlock.style.removeProperty(
					"--wishlist-btn-bg-color"
				);
				wishlistButtonBlock.style.removeProperty(
					"--wishlist-btn-text-color"
				);
				wishlistButtonBlock.style.removeProperty(
					"--wishlist-icon-stroke"
				);
				wishlistButtonBlock.style.removeProperty(
					"--wishlist-icon-fill"
				);
			}

			styleElement.textContent = `
			.wk-cta-button,
			.bndlr-add-to-cart {
			  display: ${isCartLocked ? "none" : "block"} !important;
			  pointer-events: ${isCartLocked ? "none" : "auto"} !important;
			  transition: opacity 0.3s ease-in-out !important;
			  text-align: center !important;
			  justify-content: center !important;
			}
			.bndlr-product-price,
			.bndlr-total-price {
			  visibility: ${isCartLocked ? "hidden" : "visible"} !important;
			  opacity: ${isCartLocked ? "0" : "1"} !important;
			  transition: visibility 0s, opacity 0.3s ease-in-out !important;
			}
			.wk-controls .wk-password-form {
			  visibility: ${isFeatureEnabled && isCartLocked ? "visible" : "hidden"} !important;
			  opacity: ${isFeatureEnabled && isCartLocked ? "1" : "0"} !important;
			  transition: visibility 0.3s, opacity 0.3s ease-in-out !important;
			}
	  
			${
				!isCartLocked
					? `
			wishlist-button-block:not(#wishlist-button-mobile) wk-button > button {
			  border: 1px solid #ccc !important;
			  background-color: transparent !important;
			  color: #000 !important;
			  transition: border 0.3s ease, background-color 0.3s ease, color 0.3s ease !important;
			  box-sizing: border-box !important;
			}
			wishlist-button-block:not(#wishlist-button-mobile) wk-button > button:hover {
			  border: 1px solid #000 !important;
			  background-color: #fff !important;
			}
			wishlist-button-block:not(#wishlist-button-mobile) wk-button .wk-icon {
			  --icon-stroke: #000 !important;
			  --icon-fill: ${isWishlisted ? "#000" : "transparent"} !important;
			}
			`
					: ""
			}
		  `;
		};

		updateWishlistButtonStyle(true);

		// If the wishlist button isn't rendered on the page, don't set up
		// MutationObservers/listeners (prevents unnecessary work & potential leaks).
		if (!document.querySelector("wishlist-button-block")) return;

		document.documentElement.addEventListener(
			"cart:unlock-state-changed",
			() => {
				updateWishlistButtonStyle(true);
			}
		);

		document.addEventListener("wk:login", () => {
			setTimeout(() => updateWishlistButtonStyle(true), 100);
		});

		const bundlerObserver = new MutationObserver((mutations) => {
			if (
				!this.disableAddToCart &&
				!document.querySelector(".bundler-container")
			)
				return;

			mutations.forEach((mutation) => {
				if (mutation.addedNodes.length) {
					const bundlerContainer =
						document.querySelector(".bundler-container");
					if (bundlerContainer) {
						updateWishlistButtonStyle();
					}
				}
			});
		});

		bundlerObserver.observe(document.body, {
			childList: true,
			subtree: true,
		});

		const observeWishlistButton = () => {
			const wishlistButton = document.querySelector(
				"wishlist-button-block wk-button > button"
			);
			if (wishlistButton) {
				const buttonObserver = new MutationObserver(() => {
					updateWishlistButtonStyle(false);
				});
				buttonObserver.observe(wishlistButton, {
					attributes: true,
					attributeFilter: ["class"],
				});
			}
		};

		observeWishlistButton();

		const wishlistButtonObserver = new MutationObserver(() => {
			const wishlistButton = document.querySelector(
				"wishlist-button-block wk-button > button"
			);
			if (wishlistButton) {
				updateWishlistButtonStyle(true);
				observeWishlistButton();
				wishlistButtonObserver.disconnect();
			}
		});
		wishlistButtonObserver.observe(document.body, {
			childList: true,
			subtree: true,
		});

		document.addEventListener("wk:wishlist:add:success", () => {
			setTimeout(() => updateWishlistButtonStyle(true), 50);
		});

		document.addEventListener("wk:wishlist:remove:success", () => {
			setTimeout(() => updateWishlistButtonStyle(true), 50);
		});

		const addAllButton = document.querySelector(
			"wishlist-add-to-cart wk-button"
		);
		if (addAllButton) {
			const isCartUnlocked =
				localStorage.getItem("cartUnlocked") === "true";
			addAllButton.classList.toggle("atc-locked", !isCartUnlocked);
		}
	}

	handleDynamicContent() {
		document.addEventListener("ed:ajaxinate:loaded", () => {
			if (this.#isUnlocked) {
				requestAnimationFrame(() => {
					const newElements =
						this.#container.querySelectorAll(".atc-locked");
					newElements.forEach((element) =>
						element.classList.remove("atc-locked")
					);
				});
			}
		});

		document.addEventListener("ed:filters:rendered", () => {
			if (this.#isUnlocked) {
				requestAnimationFrame(() => {
					const newElements =
						this.#container.querySelectorAll(".atc-locked");
					newElements.forEach((element) =>
						element.classList.remove("atc-locked")
					);
				});
			}
		});

		document.addEventListener("quick-buy:open", (event) => {
			if (this.#isUnlocked) {
				requestAnimationFrame(() => {
					const popover = document.querySelector(".ed-popover-inner");
					if (popover) {
						const lockedElements =
							popover.querySelectorAll(".atc-locked");
						lockedElements.forEach((element) =>
							element.classList.remove("atc-locked")
						);
					}
				});
			}
		});

		const productGrid = document.querySelector(".product-list");
		if (productGrid) {
			const observer = new MutationObserver((mutations) => {
				mutations.forEach((mutation) => {
					if (mutation.addedNodes.length && this.#isUnlocked) {
						mutation.addedNodes.forEach((node) => {
							if (node.nodeType === 1) {
								const lockedElements =
									node.querySelectorAll(".atc-locked");
								lockedElements.forEach((element) =>
									element.classList.remove("atc-locked")
								);
							}
						});
					}
				});
			});

			observer.observe(productGrid, {
				childList: true,
				subtree: true,
			});
		}
	}
}
